import { Analytics } from '@analytics';
import DownloadSvg from '@assets/images/tip_direct/Download.svg';
import QRSvg from '@assets/images/tip_direct/QR.svg';
import TopNav from '@components/Nav/TopNav';
import TipDirectLeftMenu from '@components/Nav/TopNav/TipDirectLeftMenu';
import localizationMessages from '@components/Resources/ResourcesTable/messages';
import AccessControlledButton from '@components/TapToTip/AccessControlledButton';
import AgentProfileSummaryButton from '@components/TapToTip/AgentProfileSummaryButton';
import InviteGuideModal from '@components/TapToTip/InviteGuideModal';
import InviteMenuButton from '@components/TapToTip/InviteMenuButton';
import { regionalURLs } from '@constants';
import { Button, CircularProgress, Paper, Snackbar, Tooltip } from '@material-ui/core';
import { Alert as MuiAlert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';
import { StyleBreakpoints } from '@utils/constants';
import { useIsFeatureForCompanyEnabled } from '@utils/hooks';
import { useInjectReducer } from '@utils/injectReducer';
import { useInjectSaga } from '@utils/injectSaga';
import { generateBusinessCardPrintout } from '@utils/printing_templates/generateBusinessCardPrintout';
import JSZip from 'jszip';
import MaterialTable from 'material-table';
import QRCode from 'qrcode';
import { default as React, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import * as actions from './actions';
import { StatusType, UserType } from './constants';
import messages from './messages';
import reducer from './reducer';
import saga from './saga';

const useStyles = makeStyles(theme => ({
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  },
  // Blends the material-table into the surrounding slate-50 card: transparent
  // toolbar, a single header divider, no per-row / bottom borders, and a subtle
  // blue-grey zebra stripe (the old #FAF7FA read as a warning/error tint).
  tableSurface: {
    background: 'transparent',
    boxShadow: 'none',
    '& .MuiToolbar-root': {
      backgroundColor: 'transparent'
    },
    '& .MuiTableCell-root': {
      borderBottom: 'none'
    },
    '& .MuiTableCell-head': {
      backgroundColor: '#f8fafc',
      borderBottom: '1px solid #e2e8f0',
      color: '#475569'
    },
    '& .MuiTableBody-root .MuiTableRow-root:nth-child(even)': {
      backgroundColor: '#f1f5f9'
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      backgroundColor: '#eef2f7'
    },
    '& .MuiTablePagination-root': {
      backgroundColor: 'transparent'
    },
    '& .MuiTableFooter-root .MuiTableCell-root': {
      borderTop: 'none',
      borderBottom: 'none'
    }
  },
  guideQRsText: {
    marginRight: '4px',
    minWidth: '76px'
  }
}));

const agentStatusBadgeClasses = {
  pending: 'bg-slate-100 text-slate-600 border-slate-200/55',
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200/55',
  expired: 'bg-red-50 text-red-700 border-red-200/55'
};

// eslint-disable-next-line no-unused-vars
const AgentStatus = ({ status, classes }) => {
  return (
    <div
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold border border-solid ${agentStatusBadgeClasses[
        status
      ] || agentStatusBadgeClasses.pending}`}>
      {StatusType[status]}
    </div>
  );
};

/*
 * Card 235: this used to sit beside the guide's name spelling out "Review
 * first" / "Tip first", which left the name column too narrow: the name
 * wrapped onto a second line and every row grew taller. It now rides in the
 * Status column as a two-letter pill, so it costs ~36px instead of ~90px and
 * the tooltip carries the meaning.
 *
 * Card 118: still only rendered for guides who have been switched off the
 * company default, so the column stays quiet for everyone else. Without it an
 * override is invisible until you open the dropdown.
 */
const ReviewFirstBadge = ({ reviewFirst }) => {
  const intl = useIntl();

  if (typeof reviewFirst !== 'boolean') return null;

  return (
    <Tooltip
      placement="top"
      title={intl.formatMessage(
        reviewFirst ? messages.reviewFirstOnBadgeTitle : messages.reviewFirstOffBadgeTitle
      )}>
      <span className="shrink-0 inline-flex items-center px-2 py-1 rounded-md text-xs font-bold border border-solid whitespace-nowrap bg-indigo-50 text-indigo-700 border-indigo-200/55 cursor-default">
        {intl.formatMessage(
          reviewFirst ? messages.reviewFirstOnBadge : messages.reviewFirstOffBadge
        )}
      </span>
    </Tooltip>
  );
};

const AgentsProfilesTable = ({
  agentsProfiles,
  sendInvitesToGuides,
  revokeInvite,
  fetchAgentsProfiles,
  addToursToProfile,
  activeTours,
  fetchActiveTours,
  setGuideReviewFirst,
  user,
  isLoading
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [showAlert, setShouldShowCopyAlert] = useState(false);
  const [showWarning, setshowWarning] = useState(false)
  const [isQRCodeLoading, setIsQRCodeLoading] = useState(false);
  const [isBusinessCardDownloading, setIsBusinessCardDownloading] = useState(false);
  const guides = agentsProfiles?.filter(guide => guide.type === 'user' || guide.type === 'admin');

  useInjectReducer({ key: 'tipAgents', reducer });
  useInjectSaga({ key: 'tipAgents', saga });


  function startCase(str) {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }


  const handleQRDownload = async (url, firstName, lastName) => {
    const data = await QRCode.toDataURL(url).catch(err => {
      console.error(err);
    });

    let a = document.createElement('a');
    a.download = `Tip-Direct-${firstName || ''}-${lastName || ''}.png`;
    a.href = data;
    a.click();
  };

  const downloadQRCodesAsZip = async () => {
    await new Promise(resolve => {
      setTimeout(resolve, 1000); // Simulate a delay of 1000 milliseconds (1 second)
    });

    Analytics.track('download all QR codes clicked', {
      page: 'users table'
    });
    const zip = new JSZip();
    const promises = [];

    agentsProfiles.forEach((guide, index) => {
      if (guide.type !== 'user' && guide.type !== 'admin') return;

      const { userId, firstName, lastName } = guide;
      const url = (regionalURLs[process.env.REGION] || regionalURLs.US) + `t/g/${userId}`;

      const qrFileName = `${firstName || ''}-${lastName || ''}.png`;
      const promise = QRCode.toDataURL(url)
        .then(data => {
          zip.file(qrFileName, data.substring(data.indexOf(',') + 1), { base64: true });
        })
        .catch(err => {
          console.error(`Error generating QR code for guide at index ${index}:`, err);
        });
      promises.push(promise);
    });

    await Promise.all(promises);

    const zipData = await zip.generateAsync({ type: 'blob' });
    const zipFileName = 'QR_Codes.zip';
    const a = document.createElement('a');
    a.download = zipFileName;
    a.href = URL.createObjectURL(zipData);
    a.click();
  };

  const downloadAllQRCodes = async () => {
    try {
      setIsQRCodeLoading(true);
      await downloadQRCodesAsZip();
    } catch (err) {
      console.error('Error downloading QR codes:', err);
    } finally {
      setIsQRCodeLoading(false);
    }
  };

  const handleDownloadBusinessCards = () => {
    setIsBusinessCardDownloading(true);
    if(guides && guides.length > 60){
      setshowWarning(true)
    }
    setTimeout(async () => {
      try {
        await generateBusinessCardPrintout(user, process.env.REGION, true, guides);
        Analytics.track('download all business cards clicked');
      } catch (err) {
        console.error('Error downloading business cards:', err);
      } finally {
        setshowWarning(false)
        setIsBusinessCardDownloading(false);
      }
    }, 500);
  };

  useEffect(() => {
    if (!agentsProfiles?.length) {
      fetchAgentsProfiles();
    }

    if (!activeTours?.length) {
      fetchActiveTours();
    }
  }, []);

  const isFeatureForCompanyEnabled = useIsFeatureForCompanyEnabled();
  const isReviewCollectionEnabled = isFeatureForCompanyEnabled('PostTipReviewCollection');
  // Card 118: what a guide inherits when they have no override of their own.
  const companyDirectToPostTip = Boolean(user?.companyDetails?.features?.directToPostTip);

  const columns = [
    {
      field: 'status',
      title: intl.formatMessage(messages.status),
      // nowrap on both: the status badge and the RF pill must stay on one line
      // whatever the column ends up being sized to.
      headerStyle: { whiteSpace: 'nowrap' },
      cellStyle: { whiteSpace: 'nowrap' },
      render: rowData => (
        <div className="flex flex-nowrap items-center gap-1.5">
          <AgentStatus status={rowData.status} classes={classes} />
          <ReviewFirstBadge reviewFirst={rowData.reviewFirst} />
        </div>
      )
    },
    {
      field: 'type',
      title: intl.formatMessage(messages.type),
      render: rowData => UserType[rowData.type]
    },
    {
      field: 'name',
      title: intl.formatMessage(messages.name),
      // Card 235: the review-first badge used to live here and squeezed the
      // name into wrapping. It is in the Status column now.
      render: rowData => `${rowData.firstName || ''} ${rowData.lastName || ''}`,
      customSort: (a, b) => {
        return `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(
          `${b.firstName || ''} ${b.lastName || ''}`
        );
      },
      customFilterAndSearch: (term, rowData) => {
        const terms = term.toLowerCase().split(/\s+/);
        const fullName = `${rowData.firstName || ''} ${rowData.lastName || ''}`.toLowerCase();
        return terms.every(t => fullName.includes(t));
      },
    },
    {
      field: 'email',
      title: intl.formatMessage(messages.email)
    },
    {
      field: 'cities',
      title: 'Cities',
      render: rowData => {
        const { cities } = rowData;
        const formattedCities =
          Array.isArray(cities) && cities.length > 0
            ? cities
                .map(city => startCase(city))
                .join(', ')
            : '—';

        return <div className="text-slate-600">{formattedCities}</div>;
      }
    },
    {
      field: 'numberOfCards',
      title: intl.formatMessage(messages.numberOfCards),
      render: rowData => rowData.assignedCardsAmount,
      customSort: (a, b) => a.assignedCardsAmount - b.assignedCardsAmount
    },
    {
      field: 'assignedTours',
      title: intl.formatMessage(messages.assignedTours),
      render: rowData => (rowData.type === 'invite' ? '' : rowData.tours.length),
      customSort: (a, b) => a.tours?.length - b.tours?.length
    },
    {
      title: intl.formatMessage(messages.manage),
      field: 'manage',
      sorting: false,
      headerStyle: {
        paddingRight: '25px',
        textAlign: 'right'
      },
      cellStyle: {
        textAlign: 'right'
      },
      render: rowData => {
        if (rowData.type === 'user' || rowData.type === 'admin') {
          return (
            <AgentProfileSummaryButton
              activeTours={activeTours}
              profile={rowData}
              addToursToProfile={addToursToProfile}
              userId={rowData.userId}
              handleQRDownload={handleQRDownload}
              setShouldShowCopyAlert={setShouldShowCopyAlert}
              isReviewCollectionEnabled={isReviewCollectionEnabled}
              companyDirectToPostTip={companyDirectToPostTip}
              setGuideReviewFirst={setGuideReviewFirst}
            />
          );
        }

        if (rowData.type === 'invite') {
          return (
            <InviteMenuButton
              revokeInvite={revokeInvite}
              inviteId={rowData.inviteId}
              sendInvite={sendInvitesToGuides}
              inviteeEmail={rowData.email}
            />
          );
        }
      }
    }
  ];

  return (
    <>
      <TopNav />
      <div className={classes.outerContainer}>
        <TipDirectLeftMenu selectedTab={'Users'} />
        <div className="flex-1 flex flex-col p-6 md:p-8 w-full max-w-7xl mx-auto min-w-0">
          <div className="mb-8 shrink-0">
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
              {intl.formatMessage(messages.users)}
            </h1>
            <p className="text-slate-500 mt-2">
              Manage your team, invites, QR codes and business cards.
            </p>
          </div>
          <div className="flex flex-wrap gap-[15px] mb-5 ml-auto shrink-0">
            <Button
              disabled={!agentsProfiles?.length || isBusinessCardDownloading}
              className="!bg-white hover:!bg-slate-50 !text-slate-700 !font-semibold !px-5 !py-2.5 !rounded-xl !border !border-solid !border-slate-300 !normal-case disabled:!text-slate-400"
              endIcon={<img src={DownloadSvg} alt="Download" />}
              onClick={handleDownloadBusinessCards}>
              <span className={classes.guideQRsText}>
                {isBusinessCardDownloading ? <CircularProgress size={18} /> : 'Business Cards'}
              </span>
            </Button>
            <Button
              disabled={!agentsProfiles?.length || isQRCodeLoading}
              className="!bg-white hover:!bg-slate-50 !text-slate-700 !font-semibold !px-5 !py-2.5 !rounded-xl !border !border-solid !border-slate-300 !normal-case disabled:!text-slate-400"
              startIcon={<img src={QRSvg} alt="QR" />}
              endIcon={<img src={DownloadSvg} alt="Download" />}
              onClick={downloadAllQRCodes}>
              <span className={classes.guideQRsText}>
                {isQRCodeLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  intl.formatMessage(messages.guideQRs)
                )}
              </span>
            </Button>
            <AccessControlledButton
              shouldCheckFeature={false}
              elementStyle="!px-5 !py-2.5 !border !border-solid !border-transparent !normal-case"
              title={intl.formatMessage(messages.inviteGuides)}
              onClick={() => {
                Analytics.track('invite guides open');
                setIsInviteModalOpen(true);
              }}
            />
          </div>

          <div className="w-full h-fit bg-slate-50 rounded-3xl shadow-sm border border-solid border-slate-200 p-4 sm:p-6 shrink-0">
          <MaterialTable
            isLoading={isLoading}
            style={{ width: '100%', borderRadius: '16px', background: 'transparent' }}
            title=""
            columns={columns}
            data={agentsProfiles}
            components={{
              // eslint-disable-next-line react/display-name
              Container: props => (
                <Paper
                  {...props}
                  elevation={0}
                  className={`${props.className || ''} ${classes.tableSurface}`}
                  style={{ ...props.style, background: 'transparent', boxShadow: 'none' }}
                />
              )
            }}
            options={{
              pageSize: 10,
              tableLayout: 'auto',
              emptyRowsWhenPaging: false,
              headerStyle: {
                whiteSpace: 'nowrap',
                position: 'sticky',
                top: '0'
              },
              draggable: false,
              // Cells must be allowed to wrap: nowrap pushed the table wider than
              // the page frame and clipped the Manage column off the card edge.
              cellStyle: {
                width: 'fit-content'
              },
              maxBodyHeight: window.innerWidth > StyleBreakpoints.sm ? '75vh' : '55vh'
            }}
            localization={{
              pagination: {
                labelRowsSelect: intl.formatMessage(localizationMessages.labelRowsSelect),
                labelRowsPerPage: intl.formatMessage(localizationMessages.labelRowsPerPage),
                firstAriaLabel: intl.formatMessage(localizationMessages.firstAriaLabel),
                firstTooltip: intl.formatMessage(localizationMessages.firstTooltip),
                previousAriaLabel: intl.formatMessage(localizationMessages.previousAriaLabel),
                previousTooltip: intl.formatMessage(localizationMessages.previousTooltip),
                nextAriaLabel: intl.formatMessage(localizationMessages.nextAriaLabel),
                nextTooltip: intl.formatMessage(localizationMessages.nextTooltip),
                lastAriaLabel: intl.formatMessage(localizationMessages.lastAriaLabel),
                lastTooltip: intl.formatMessage(localizationMessages.lastTooltip)
              },
              toolbar: {
                searchTooltip: intl.formatMessage(localizationMessages.searchTooltip),
                searchPlaceholder: intl.formatMessage(localizationMessages.searchPlaceholder)
              },
              body: {
                emptyDataSourceMessage: intl.formatMessage(
                  localizationMessages.emptyDataSourceMessage
                )
              }
            }}
          />
          </div>

          <InviteGuideModal
            isOpen={isInviteModalOpen}
            setIsOpen={setIsInviteModalOpen}
            sendInvitesToGuides={sendInvitesToGuides}
            companyName={user.companyName}
            inviteCode={user.inviteCode}
          />
        </div>
      </div>

      <Snackbar
        open={showWarning}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <MuiAlert
          variant="filled"
          elevation={6}
          severity="warning"
        >
          Warning; you are downloading a lot of files, this may take some time.
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={showAlert}
        autoHideDuration={10000}
        onClose={() => {
          setShouldShowCopyAlert(false);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <MuiAlert
          variant="filled"
          elevation={6}
          severity="success"
          onClose={() => {
            setShouldShowCopyAlert(false);
          }}>
          {intl.formatMessage(messages.copiedToClipboard)}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

const mapStateToProps = state => ({
  agentsProfiles: state.tipAgents?.agentsProfiles,
  activeTours: state.tipAgents?.activeTours,
  isLoading: state.tipAgents?.isLoading,
  user: state.user?.user
});

const mapDispatchToProps = dispatch => ({
  fetchAgentsProfiles: () => dispatch(actions.fetchAgentsProfiles()),
  sendInvitesToGuides: payload => dispatch(actions.sendInvitesToGuides(payload)),
  addToursToProfile: (userId, tourIds) => dispatch(actions.addToursToProfile({ userId, tourIds })),
  revokeInvite: payload => dispatch(actions.revokeInvite(payload)),
  fetchActiveTours: () => dispatch(actions.fetchActiveTours()),
  setGuideReviewFirst: (userId, reviewFirst) =>
    dispatch(actions.setGuideReviewFirst({ userId, reviewFirst }))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AgentsProfilesTable);
